import React, { ComponentProps, useEffect, useRef, useState } from "react"
import dynamic from "next/dynamic"
import type Widget from "@harisenin/react-floating-whatsapp"
import { onFirstInteraction } from "@lib/functions/third-party"

const Floating = dynamic(() => import("@harisenin/react-floating-whatsapp"), { ssr: false })

/**
 * The widget keeps its chat box `aria-hidden` (even when open) with a focusable input and button
 * inside. Keep it inert and hidden from AT only while closed (inline height 0), and expose it when open.
 */
function syncChatBox(root: HTMLElement) {
  root.querySelectorAll<HTMLElement>("div:has(> footer form)").forEach((box) => {
    const closed = box.style.height === "0px"
    box.inert = closed
    if (closed) box.setAttribute("aria-hidden", "true")
    else box.removeAttribute("aria-hidden")
  })
}

/** Mounts the WhatsApp widget (and its JS) on the visitor's first interaction. */
export function FloatingWhatsApp(props: ComponentProps<typeof Widget>) {
  const [mounted, setMounted] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => onFirstInteraction(() => setMounted(true)), [])

  useEffect(() => {
    const root = rootRef.current
    if (!mounted || !root) return
    const observer = new MutationObserver(() => syncChatBox(root))
    observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ["style"] })
    syncChatBox(root)
    return () => observer.disconnect()
  }, [mounted])

  return <div ref={rootRef} className="contents">{mounted && <Floating {...props} />}</div>
}
